import React, { Fragment, useCallback, useState } from 'react';
import useEvent from '../../hooks/useEvent';

export const DialogsContext = React.createContext<{
  pushDialog: <PropType extends object, ResultType>(params: {
    render: (
      props: PropType | null,
      onFinish: (result: ResultType) => void,
    ) => React.ReactElement;
    props: PropType | null;
  }) => Promise<ResultType>;
}>({
  pushDialog: async () => {
    throw new Error('DialogsContext not initialized');
  },
});

export type UseDialogParams<PropsType, ResultType> = (
  props: PropsType,
  onFinish: (result: ResultType) => void,
) => React.ReactElement;

/** Initialize a custom dialog component. Returns a function that can be called to open the dialog.
 * The function itself returns a promise that resolves with the result of the dialog.
 * @example
 * const dialog = useDialog((props: { msg: string }, onFinish: () => void) =>
 *  <Modal onClose={() => onFinish()}>
 *    {msg}
 *  </Modal>));
 * ...
 * const result = await dialog({ msg: 'Close the dialog to continue!' });
 * */
export function useDialog<PropType extends object, ResultType>(
  params: UseDialogParams<PropType, ResultType>,
) {
  const dialogsContext = React.useContext(DialogsContext);

  return useEvent(async (props: PropType) => {
    const result = await dialogsContext.pushDialog({
      render: params,
      props,
    });

    return result;
  });
}

export const DialogsProvider = (props: {
  children: React.ReactNode;
}): JSX.Element => {
  const [dialogs, setDialogs] = useState<
    Array<{
      render: (
        props: unknown,
        onFinish: (result: unknown) => void,
      ) => React.ReactElement;
      props: object;
      resolve: (result: unknown) => void;
    }>
  >([]);

  const pushDialog = useCallback(
    <PropType extends object, ResultType>({
      render,
      props,
    }: {
      render: (
        props: PropType,
        onFinish: (result: ResultType) => void,
      ) => React.ReactElement;
      props: PropType;
    }) => {
      return new Promise<ResultType>((resolve) => {
        setDialogs((dialogs) => [
          ...dialogs,
          {
            render,
            props,
            resolve,
          },
        ]);
      });
    },
    [],
  );

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <DialogsContext.Provider value={{ pushDialog: pushDialog as any }}>
      {props.children}
      {dialogs.map(({ render, props, resolve }, index) => (
        <Fragment key={index}>
          {render({ ...props, index }, (result) => {
            resolve(result);
            setDialogs((dialogs) => dialogs.filter((_, i) => i !== index));
          })}
        </Fragment>
      ))}
    </DialogsContext.Provider>
  );
};
